import { supabase } from './supabaseClient'; // Your initialized Supabase client

type UploadOptions = {
	bucket: string;
	path?: string; // Subfolder path (e.g., 'user-uploads/')
	upsert?: boolean; // Replace file if exists
};

/**
 * Uploads a file to Supabase Storage with progress tracking.
 * @param file - File object or Blob.
 * @param fileName - Target filename (e.g., 'avatar.jpg').
 * @param options - { bucket: string, path?: string, upsert?: boolean }.
 * @returns Public URL if successful, or throws error.
 */
export const uploadToSupabaseStorage = async (
	file: File | Blob,
	fileName: string,
	options: UploadOptions
) => {
	const { bucket, path = '', upsert = false } = options;

	// 1. Validate MIME type (example: allow only images)
	const validMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
	if (file instanceof File && !validMimeTypes.includes(file.type)) {
		throw new Error('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
	}

	// 2. Generate full path (e.g., 'user-uploads/avatar.jpg')
	const filePath = path ? `${path.replace(/\/$/, '')}/${fileName}` : fileName;

	// 3. Upload with progress tracking
	const { data, error } = await supabase.storage
		.from(bucket)
		.upload(filePath, file, {
			upsert,
			contentType: file.type || 'application/octet-stream',
			cacheControl: '3600', // 1 hour cache
		});

	if (error) {
		console.error('Upload error:', error);
		throw new Error(error.message);
	}

	// 4. Get public URL (if bucket is public)
	const { data: publicUrl } = supabase.storage
		.from(bucket)
		.getPublicUrl(filePath);

	return {
		path: data.path,
		publicUrl: publicUrl.publicUrl,
	};
};